import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import "./_index.scss";

interface FormData {
  name: string;
  password: string;
  user_type: string;
  client: string;
  operation: string;
  email: string;
  email_address: string;
  telephone: string;
  start_date: string;
  end_date: string;
  expected_date: string;
  client_id: string;
  forecast_id: string;
}

interface OperationData {
  forecast_id: string;
  forecast_description: string;
  client_id: string;
}

const AVATAR_COLORS = [
  "#818cf8","#38bdf8","#fbbf24","#34d399","#f87171",
  "#e879f9","#a78bfa","#2dd4bf","#fb923c","#22d3ee",
];

const formatDate = (d: string) => {
  if (!d) return "Not specified";
  try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
};

const getStatus = (exp: string) => exp && new Date(exp) > new Date() ? "Active" : "Inactive";

const ViewUserComponent = () => {
  const [sn, setSn] = useState(localStorage.getItem("sideNav"));
  const data = useSelector((state: any) => state?.app);
  const windowWidth = useRef(window.innerWidth);
  const [open, setOpen] = useState(windowWidth.current > 1000 ? true : false);
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: "", password: "", user_type: "", client: "", operation: "",
    email: "", email_address: "", telephone: "", start_date: "",
    end_date: "", expected_date: "", client_id: "", forecast_id: "",
  });
  const [projectLocation, setProjectLocation] = useState<string>("");
  const { id } = useParams();
  const navigate = useNavigate();

  const getProjectLocation = async (clientId: string, forecastId: string, fallbackOperation: string) => {
    if (!clientId || !forecastId) return fallbackOperation || "Not specified";
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/operations12/${clientId}/`);
      const operations: OperationData[] = response.data;
      const matchingOperation = operations.find(op => op.forecast_id?.toString() === forecastId?.toString());
      if (matchingOperation) return matchingOperation.forecast_description;
      return fallbackOperation || "Not specified";
    } catch (error) {
      console.error("Error fetching operations:", error);
      return fallbackOperation || "Not specified";
    }
  };

  useEffect(() => {
    if (id) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_IP}/api/user/get/${id}/`);
          const userData = response.data;
          setFormData(userData);
          const location = await getProjectLocation(userData.client_id, userData.forecast_id, userData.operation);
          setProjectLocation(location);
        } catch (error) {
          console.error("Error:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [id]);

  useEffect(() => { setSn(localStorage.getItem("sideNav")); }, []);
  useEffect(() => { setOpen(data.toggle); }, [data]);

  const avatarColor = AVATAR_COLORS[Number(id || 0) % AVATAR_COLORS.length];
  const status = getStatus(formData.expected_date);
  const initials = formData.name ? formData.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() : "?";

  if (loading) return null;

  return (
    <div style={open ? { marginLeft: 260, width: "calc(100% - 260px)" } : {}} className="mainas">
      <div className="vu-wrap">

        <div className="vu-header">
          <div className="vu-header-left">
            <button className="vu-back-btn" onClick={() => navigate("/usermanagement")}>
              <ArrowBackIcon />
            </button>
            <div>
              <h1 className="vu-heading">View User</h1>
              <p className="vu-subheading">View complete user information</p>
            </div>
          </div>
      
        </div>


        <div className="vu-grid">
          <div className="vu-detail-card">
            <div className="vu-detail-icon vu-detail-icon--blue"><BusinessOutlinedIcon /></div>
            <div className="vu-detail-body">
              <span className="vu-detail-label">Client</span>
              <span className="vu-detail-val">{formData.client || "Not specified"}</span>
            </div>
          </div>
          <div className="vu-detail-card">
            <div className="vu-detail-icon vu-detail-icon--indigo"><LocationOnOutlinedIcon /></div>
            <div className="vu-detail-body">
              <span className="vu-detail-label">Project Location</span>
              <span className="vu-detail-val">{projectLocation || "Not specified"}</span>
            </div>
          </div>
          <div className="vu-detail-card">
            <div className="vu-detail-icon vu-detail-icon--purple"><AdminPanelSettingsOutlinedIcon /></div>
            <div className="vu-detail-body">
              <span className="vu-detail-label">User Type</span>
              <span className="vu-detail-val" style={{ textTransform: "capitalize" }}>{formData.user_type || "Not specified"}</span>
            </div>
          </div>
          <div className="vu-detail-card">
            <div className="vu-detail-icon vu-detail-icon--teal"><EmailOutlinedIcon /></div>
            <div className="vu-detail-body">
              <span className="vu-detail-label">Email Address</span>
              <span className="vu-detail-val">{formData.email || formData.email_address || "Not specified"}</span>
            </div>
          </div>
          <div className="vu-detail-card">
            <div className="vu-detail-icon vu-detail-icon--green"><CalendarTodayOutlinedIcon /></div>
            <div className="vu-detail-body">
              <span className="vu-detail-label">Start Date</span>
              <span className="vu-detail-val">{formatDate(formData.start_date)}</span>
            </div>
          </div>
          <div className="vu-detail-card">
            <div className="vu-detail-icon vu-detail-icon--amber"><EventOutlinedIcon /></div>
            <div className="vu-detail-body">
              <span className="vu-detail-label">Expected End Date</span>
              <span className="vu-detail-val">{formatDate(formData.expected_date)}</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ViewUserComponent;